
import { v4 as uuidv4 } from "uuid";
import { Message } from "@/types/message";
import { getOpenAIResponse } from "@/utils/openai";
import { AssistantData } from "@/types/assistant";
import { checkChatCache, storeChatInCache, normalizeQuestion } from "@/utils/cache";
import { getIsCacheEnabled } from "@/utils/cacheSettings";
import { supabase } from "@/integrations/supabase/client";
import { trackQuestion } from "@/utils/analytics";
import { debugLogger } from "@/utils/debugLogger";

export const processChatMessage = async (
  content: string,
  messages: Message[],
  assistantData: AssistantData | null
): Promise<Message> => {
  let response;
  let isCached = false;
  let startTime = performance.now();
  
  try {
    debugLogger.log("⭐ processChatMessage: Started with question:", content.substring(0, 50));
    
    // Normalize the question for analytics tracking
    const normalizedQuestion = normalizeQuestion(content);
    
    // Check if caching is enabled
    const isCacheEnabled = getIsCacheEnabled();
    
    // Only check cache if caching is enabled and we have a hotel_id from assistantData
    if (isCacheEnabled && assistantData?.hotel_id) {
      // Check if this question is in the cache with conversation context
      const cachedResponse = await checkChatCache(content, assistantData.hotel_id, messages);
      
      // If we found a cached answer, use it
      if (cachedResponse) {
        debugLogger.log("⭐ processChatMessage: Using smart cached response", {
          isFollowUp: cachedResponse.isFollowUp,
          contextHash: cachedResponse.contextHash
        });
        isCached = true;
        response = cachedResponse;
      }
    }
    
    // If not cached or cache lookup failed, query OpenAI
    if (!response) {
      debugLogger.log("⭐ processChatMessage: Querying OpenAI for fresh response");
      
      const openaiMessages = messages
        .filter(msg => !msg.error)
        .map((msg) => ({
          role: msg.isBot ? "assistant" : "user",
          content: msg.content,
        }));
      
      // Add the current user message
      openaiMessages.push({
        role: "user",
        content,
      });
      
      // Get response from OpenAI via our edge function
      response = await getOpenAIResponse(openaiMessages, assistantData);
      
      // Calculate response time
      const responseTime = Math.round(performance.now() - startTime);
      
      // Store the response in the cache if caching is enabled, it was successful and not an error
      if (isCacheEnabled && assistantData?.hotel_id && !response.error) {
        await storeChatInCache(
          assistantData.hotel_id, 
          content, 
          response, 
          'gpt-4o-mini',
          messages // Pass conversation context for smart caching
        );
      }
    }
    
    // Track analytics once after we have the complete response data
    if (assistantData?.hotel_id) {
      const responseTime = Math.round(performance.now() - startTime);
      const tokenCount = response.tokenCount || 0;
      const modelName = response.model || 'gpt-4o-mini';
      
      debugLogger.log("⭐ processChatMessage: Tracking question analytics:", {
        responseTime,
        tokenCount,
        model: modelName,
        cached: isCached
      });
      
      try {
        await trackQuestion({
          question: content,
          normalizedQuestion,
          hotelId: assistantData.hotel_id,
          responseTimeMs: responseTime,
          isCached: isCached,
          tokenCount: tokenCount,
          model: modelName,
          metadata: {
            assistantId: assistantData.id,
            urlSlug: assistantData.url_slug,
            promptTokens: response.promptTokens || 0,
            completionTokens: response.completionTokens || 0
          }
        });
        debugLogger.log("⭐ processChatMessage: Question analytics tracked successfully");
      } catch (analyticsError) {
        debugLogger.error("⭐ processChatMessage: Failed to track question analytics:", analyticsError);
      }
    }
    
    // Create the bot message
    return {
      id: uuidv4(),
      content: response.message,
      isBot: true,
      timestamp: new Date(),
      systemPrompt: response.systemPrompt || "Prompt managed in Edge Function",
      fullPromptContext: response.fullPromptContext || null,
      error: response.error || false,
      errorType: response.errorType || undefined,
      errorDetails: response.errorDetails || undefined,
      statusCode: response.statusCode,
      edgeFunctionResponse: response,
      cached: isCached || response.cached || false,
      model: response.model || 'gpt-4o-mini'
    };
  } catch (error) {
    debugLogger.error("⭐ processChatMessage: Error getting response:", error);
    
    let errorDetails = "";
    let errorType = "technical_error";
    let statusCode = 500;
    
    if (error instanceof Error) {
      errorDetails = `${error.name}: ${error.message}\n${error.stack || ""}`;
    } else {
      errorDetails = String(error);
    }
    
    // Add an error message to the chat
    return {
      id: uuidv4(),
      content: "Sorry, I couldn't process that request. There was an error connecting to the OpenAI API.",
      isBot: true,
      timestamp: new Date(),
      error: true,
      errorType: errorType,
      errorDetails: errorDetails,
      statusCode: statusCode
    };
  }
};
